// React
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";

// Internal
import TestFooter from "../../components/layouts/Footer/TestFooter";
import Header from "../../components/layouts/Header/Header";
import AggregationControls from "./components/AggregationControls/AggregationControls.js";
import GraphRenderer from "./components/GraphRenderer.js";
import GraphCatalogRail from "./components/GraphCatalogRail/GraphCatalogRail.js";
import DayRecordPanel from "./components/DayRecordPanel/DayRecordPanel.js";
import { ValuesIcon } from "../../components/ui/Icons/Icon.js";
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";
import { usePersistentLoading } from "../../hooks/usePersistentLoading.js";
import { hasScore } from "../../utils/dayProgress";
import { useLoadingContent } from "../../hooks/useLoadingContent.js";
import { useLoadingFloor } from "../../hooks/useLoadingFloor.js";
import useResponsiveState from "../../hooks/useResponsiveState";
import SEO from "../../components/seo/SEO.js";

// Styling
import "./insightsscreen.css";

// UI Config — nine graphs, grouped by what they actually do. `glyph` picks the
// rail's mini chart shape; `unit` is the scale that graph reports in.
const availableGraphs = {
  TimeSeries: [
    {
      id: "feeling_score",
      label: "How you felt across the day.",
      explainer:
        "Your end-of-day feeling out of 5. Higher means you ended the day feeling better.",
      glyph: "bars",
      unit: "0–5",
    },
    {
      id: "tasks_completed",
      label: "How much you did.",
      explainer:
        "The sum of your moment scores for the day (max 50). Shows how much you actually did, regardless of plan.",
      glyph: "bars",
      unit: "0–50",
    },
    {
      id: "tasks_completion_rate",
      label: "How much of your plan that you did.",
      explainer:
        "The share of your planned moments you actually carried out. 100% means you did everything you set out to do.",
      glyph: "line",
      unit: "0–100%",
    },
    {
      id: "wave_score",
      label: "Your wave score.",
      explainer:
        "A composite of how much you did, how efficiently you did it, and how you felt. One number to track overall day quality.",
      glyph: "line",
      unit: "0–15",
    },
  ],
  Regression: [
    {
      id: "feeling_doing",
      label: "How you felt and how much you did.",
      explainer:
        "Does doing more lead to feeling better? Each dot is a day — bottom-left is low-effort/low-mood, top-right is high-effort/high-mood.",
      glyph: "scatter",
    },
    {
      id: "morning_evening",
      label: "How you started vs. how you ended.",
      explainer:
        "How your morning score predicted your evening score. Points above the diagonal mean the day got better.",
      glyph: "scatter",
    },
    {
      id: "doing_shift",
      label: "How much you did vs. how much your day shifted.",
      explainer:
        "Did doing more lift your mood across the day? X is average moment score, Y is the change from morning to evening.",
      glyph: "scatter",
    },
    {
      id: "carryover",
      label: "Yesterday's end vs. today's start.",
      explainer:
        "Does last night's mood spill into this morning? X is yesterday evening, Y is today morning, for consecutive days only.",
      glyph: "scatter",
    },
  ],
  Combination: [
    {
      id: "moment_mix",
      label: "How each moment scores vs. how often you plan it.",
      explainer:
        "For each of your 5 moments: bar = average score when it was logged, line = how often you planned it.",
      glyph: "combo",
    },
  ],
};

// Reading order of the families down the rail. They are separated by a rule
// rather than headed by labels — the headings, and their grain notes, made the
// rail read as busy against nine already-descriptive graph names.
const GRAPH_FAMILY_ORDER = ["TimeSeries", "Regression", "Combination"];

const DEFAULT_GRAPH_ID = availableGraphs.TimeSeries[0].id;

// The graph's family determines the chart component — the inverse of the old
// graphTypeForViewMode(), where viewMode decided it.
const familyOf = (graphId) =>
  GRAPH_FAMILY_ORDER.find((family) =>
    availableGraphs[family].some((graph) => graph.id === graphId),
  ) || "TimeSeries";

const graphById = (graphId) =>
  availableGraphs[familyOf(graphId)].find((graph) => graph.id === graphId);

export default function InsightsScreen({ history, props }) {
  const { viewType } = useResponsiveState();
  const isMobile = viewType === "mobile";

  const loadingContent = useLoadingContent();
  const pageLoading = useLoadingFloor(10000, loadingContent);
  const { isShowing, startLoading, stopLoading, tileProps } =
    usePersistentLoading(pageLoading, loadingContent);

  const dayList = useSelector((state) => state.dayList);
  const { loading, days } = dayList;

  const isCurrentlyFetching = loading;

  const filteredDays = useMemo(
    // hasScore, not truthiness: a day genuinely scored 0 belongs on the chart.
    () => (days || []).filter((el) => el.logDate && hasScore(el.endScore)),
    [days],
  );

  // Days the record panel can step through, oldest first.
  const orderedDays = useMemo(
    () =>
      [...filteredDays].sort(
        (a, b) => new Date(a.logDate) - new Date(b.logDate),
      ),
    [filteredDays],
  );

  const [device] = useState("desktop");
  const [graphSelected, setGraphSelected] = useState(DEFAULT_GRAPH_ID);
  const [viewMode, setViewMode] = useState("W");
  const [viewOffset, setViewOffset] = useState(0);
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [hoveredDayId, setHoveredDayId] = useState(null);
  const [chartView, setChartView] = useState({
    rangeLabel: "",
    average: null,
    visibleCount: 0,
  });

  const recordRef = useRef(null);

  const family = familyOf(graphSelected);
  const selectedGraph = graphById(graphSelected);
  const isTimeSeries = family === "TimeSeries";

  // The window resets whenever the grain or the graph changes.
  useEffect(() => {
    setViewOffset(0);
  }, [viewMode, graphSelected]);

  // On mobile the record is a reveal from a tap on this plot, so a new graph
  // invalidates it. Desktop keeps its selection: the panel is a fixed column
  // there and always has something in it.
  useEffect(() => {
    if (isMobile) setSelectedDayId(null);
  }, [graphSelected, isMobile]);

  const selectedIndex = orderedDays.findIndex((d) => d.id === selectedDayId);
  const selectedDay = selectedIndex >= 0 ? orderedDays[selectedIndex] : null;
  const pinnedDay = selectedDay || orderedDays[orderedDays.length - 1] || null;

  // Hovering the plot is what drives the day record — it replaced the tooltip.
  // The pinned day is what shows when the pointer is away.
  const hoveredDay = hoveredDayId
    ? orderedDays.find((d) => d.id === hoveredDayId) || null
    : null;

  // Mobile opens the record on a tap and on nothing else. Hover is a pointer
  // affordance, and a record standing open under every chart is what made the
  // screen read as two competing views in one viewport.
  const activeDay = isMobile ? selectedDay : hoveredDay || pinnedDay;

  const handleNodeSelect = useCallback((node) => {
    // Yearly grain aggregates to {category, value, ids[]} and moment-mix data
    // is per-moment, so neither resolves to a single day. Hold the selection.
    if (!node || node.id == null) return;
    setSelectedDayId(node.id);
  }, []);

  const handleNodeHover = useCallback(
    (node) => {
      // Nothing on mobile reads the hovered day, and a touch drag across the
      // plot fires this on every pointer move. The chart draws its own focus
      // marker in d3, so dropping the state costs nothing there.
      if (isMobile) return;
      setHoveredDayId(node && node.id != null ? node.id : null);
    },
    [isMobile],
  );

  const handleStepDay = useCallback(
    (direction) => {
      const current =
        selectedIndex >= 0 ? selectedIndex : orderedDays.length - 1;
      const next = current + direction;
      if (next < 0 || next >= orderedDays.length) return;
      setSelectedDayId(orderedDays[next].id);
    },
    [selectedIndex, orderedDays],
  );

  const handleViewStateChange = useCallback((next) => {
    setChartView((prev) =>
      prev.rangeLabel === next.rangeLabel &&
      prev.average === next.average &&
      prev.visibleCount === next.visibleCount
        ? prev
        : next,
    );
  }, []);

  useEffect(() => {
    if (isCurrentlyFetching) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isCurrentlyFetching, startLoading, stopLoading]);

  // The reveal: the sheet appears below the controls and the page comes to it,
  // so the journal entry lands in its own viewport rather than crowding the
  // plot. Scrolling back up to the chart is one flick.
  useEffect(() => {
    if (!isMobile || !selectedDayId || !recordRef.current) return;
    recordRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isMobile, selectedDayId]);

  const stepIndex = selectedIndex >= 0 ? selectedIndex : orderedDays.length - 1;

  // Yearly grain buckets days into months and moment mix aggregates every day,
  // so neither resolves a tap to a single day. No point promising a record.
  const canOpenRecord =
    family !== "Combination" && !(isTimeSeries && viewMode === "Y");

  // Built once: mobile renders it inside the chart header, desktop renders the
  // full rail as its own column.
  const graphPicker = (
    <GraphCatalogRail
      families={GRAPH_FAMILY_ORDER}
      availableGraphs={availableGraphs}
      graphSelected={graphSelected}
      onSelect={setGraphSelected}
      variant="header"
    />
  );

  const recordPanel = (
    <DayRecordPanel
      day={activeDay}
      onStepDay={handleStepDay}
      canStepBack={stepIndex > 0}
      canStepForward={stepIndex < orderedDays.length - 1}
      onClose={isMobile ? () => setSelectedDayId(null) : undefined}
    />
  );

  return (
    <>
      <SEO
        title={"Your Insights"}
        description="Discover new insights about yourself."
        canonicalUrl={`https://www.nowasteddays.io/insights`}
      />

      <Header variant="light" active={true} />
      {isShowing ? (
        <LoadingTile content={loadingContent} {...tileProps} />
      ) : !days || days.length < 1 ? (
        <div
          style={{
            width: "100%",
            height: "76vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="loading__container-v1">
            <h2>Log a day to start seeing your insights.</h2>
            <p
              style={{
                fontSize: "0.85rem",
                color: "#004a22",
                marginTop: "16px",
                opacity: 0.7,
                textAlign: "center",
              }}
            >
              NoWastedDays is free for everyone. If you find value in it,{" "}
              <a href="/donate" style={{ color: "#004a22", fontWeight: "600" }}>
                consider supporting us
              </a>{" "}
              when you're ready.
            </p>
          </div>
          <div className="loading--icon">
            <ValuesIcon />
          </div>
        </div>
      ) : (
        <div className={`insights__container ${device}`}>
          <div className="insight__space">
            {!isMobile && (
              <GraphCatalogRail
                families={GRAPH_FAMILY_ORDER}
                availableGraphs={availableGraphs}
                graphSelected={graphSelected}
                onSelect={setGraphSelected}
                totalCount={days.length}
                scoredCount={filteredDays.length}
              />
            )}

            <div className="insight__chart-col">
              <div className="insight__graph">
                <GraphRenderer
                  title={selectedGraph?.label}
                  data={filteredDays}
                  family={family}
                  graphSelected={graphSelected}
                  viewMode={viewMode}
                  viewOffset={viewOffset}
                  setViewOffset={setViewOffset}
                  onNodeSelect={handleNodeSelect}
                  onNodeHover={handleNodeHover}
                  onViewStateChange={handleViewStateChange}
                  selectedId={
                    (isMobile ? selectedDay?.id : pinnedDay?.id) ?? null
                  }
                  average={isTimeSeries ? chartView.average : null}
                  picker={isMobile ? graphPicker : null}
                />
              </div>
              <AggregationControls
                viewMode={viewMode}
                setViewMode={setViewMode}
                viewOffset={viewOffset}
                setViewOffset={setViewOffset}
                rangeLabel={isTimeSeries ? chartView.rangeLabel : "All entries"}
                disabled={!isTimeSeries}
              />
              {isMobile && canOpenRecord && !activeDay && (
                <p className="insight__record-hint">
                  Tap any point on the plot to read that day.
                </p>
              )}
            </div>

            {isMobile
              ? activeDay && (
                  <div className="insight__record-reveal" ref={recordRef}>
                    {recordPanel}
                  </div>
                )
              : recordPanel}
          </div>
        </div>
      )}
      <TestFooter />
    </>
  );
}
